import {
  RegFieldsEnum,
  RegistrationQuestion,
  RegistrationStep,
  RegistrationStepType,
} from '@/types/registration.types';

type Args = {
  shouldBePresent: boolean;
  registrationSteps: RegistrationStep[];
  registrationQuestions: RegistrationQuestion[];
  requiredRegistrationQuestions: number[] | null | undefined;
  channelRegistrationQuestions: RegistrationQuestion[];
};

type Result = {
  registrationSteps: RegistrationStep[];
  registrationQuestions: RegistrationQuestion[];
  requiredRegistrationQuestions: number[];
};

const isInPerson = (question: RegistrationQuestion): boolean => {
  return question.registration_question === RegFieldsEnum.attend_in_person;
};

/**
 * Reconciles the attend-in-person question against the in-person attendee mode setting. Modelled on
 * `applyGatingDerivedRegistrationFields`: every mutation that can disturb the general step re-runs
 * this, so the question is derived rather than destructively edited and never restored.
 *
 * Symmetric, unlike the gating helper's email rule — this question is owned solely by the in-person
 * setting, so removing it is safe. Re-adding sources the definition from the channel catalog, which
 * is what makes remove-then-restore lossless without a snapshot.
 *
 * Returns updated arrays when changes are needed, or null when the form is already in sync.
 */
const applyInPersonDerivedRegistrationFields = ({
  shouldBePresent,
  registrationSteps,
  registrationQuestions,
  requiredRegistrationQuestions,
  channelRegistrationQuestions,
}: Args): Result | null => {
  let nextSteps = registrationSteps;
  let nextQuestions = registrationQuestions;
  let nextRequired = requiredRegistrationQuestions ? [...requiredRegistrationQuestions] : [];
  let changed = false;

  // The general step is the authored artifact; questions/required follow it. They are checked
  // independently so a partially-desynced project converges rather than being left half-fixed.
  const hasInStep = registrationSteps.some(
    (step) => step.type === RegistrationStepType.general && (step.questions ?? []).some(isInPerson)
  );
  const hasInQuestions = registrationQuestions.some(isInPerson);
  const hasInRequired = nextRequired.includes(RegFieldsEnum.attend_in_person);

  if (shouldBePresent) {
    const question = channelRegistrationQuestions.find(isInPerson);
    if (!question) return null;

    if (!hasInStep) {
      nextSteps = nextSteps.map((step) => {
        if (step.type !== RegistrationStepType.general) {
          return step;
        }
        return {
          ...step,
          questions: [...(step.questions ?? []), question],
        };
      });
      changed = true;
    }

    if (!hasInQuestions) {
      nextQuestions = [...nextQuestions, question];
      changed = true;
    }

    if (!hasInRequired) {
      nextRequired = Array.from(new Set([...nextRequired, RegFieldsEnum.attend_in_person]));
      changed = true;
    }
  } else {
    if (hasInStep) {
      nextSteps = nextSteps.map((step) => {
        if (step.type !== RegistrationStepType.general) {
          return step;
        }
        return {
          ...step,
          questions: (step.questions ?? []).filter((question) => !isInPerson(question)),
        };
      });
      changed = true;
    }

    if (hasInQuestions) {
      nextQuestions = nextQuestions.filter((question) => !isInPerson(question));
      changed = true;
    }

    if (hasInRequired) {
      nextRequired = nextRequired.filter((question) => question !== RegFieldsEnum.attend_in_person);
      changed = true;
    }
  }

  if (!changed) return null;

  return {
    registrationSteps: nextSteps,
    registrationQuestions: nextQuestions,
    requiredRegistrationQuestions: nextRequired,
  };
};

export default applyInPersonDerivedRegistrationFields;
